import React, { forwardRef } from 'react'
import classNames from 'classnames'

// Typography variant to HTML element mapping (matching Ascend defaults)
const VARIANT_ELEMENT_MAP = {
  // Body variants - default to span to avoid issues in buttons/links, can be overridden with component prop
  bodyXs: 'span',
  bodySm: 'span',
  bodyMd: 'span',
  bodyLg: 'span',

  // Label variants
  labelSm: 'span',
  labelMd: 'span',
  labelLg: 'span',

  // Heading variants
  headingSm: 'h3',
  headingMd: 'h2',
  headingLg: 'h1',
  headingXl: 'h1',

  // Display variants
  displayXs: 'h4',
  displaySm: 'h3',
  displayMd: 'h2',
  displayLg: 'h1',
  displayXl: 'h1',
}

// Font weight classes
const FONT_WEIGHT_MAP = {
  light: 'font-light',
  regular: 'font-normal',
  medium: 'font-medium',
  bold: 'font-bold',
}

// Font style classes
const FONT_STYLE_MAP = {
  normal: 'not-italic',
  italic: 'italic',
}

// Color classes (matching common Ascend Typography color values)
const COLOR_MAP = {
  primary: 'text-primary',
  secondary: 'text-secondary',
  inherit: 'text-inherit',
  disabled: 'text-gray-400',
  error: 'text-red-500',
  warning: 'text-yellow-500',
  info: 'text-blue-500',
  success: 'text-green-500',
}

// Variant-specific spacing (matching Ascend Typography defaults - minimal spacing)
const VARIANT_SPACING_MAP = {
  // Body variants - minimal margins to match Ascend spacing
  bodyXs: 'mb-1',
  bodySm: 'mb-1',
  bodyMd: 'mb-1',
  bodyLg: 'mb-1',

  // Label variants - no margins
  labelSm: '',
  labelMd: '',
  labelLg: '',

  // Heading variants - minimal heading margins to match Ascend
  headingSm: 'mb-2',
  headingMd: 'mb-2',
  headingLg: 'mb-3',
  headingXl: 'mb-3',

  // Display variants - moderate margins
  displayXs: '',
  displaySm: 'mb-2',
  displayMd: 'mb-3',
  displayLg: 'mb-4',
  displayXl: 'mb-4',
}

const Typography = forwardRef(
  (
    {
      variant = 'bodyMd',
      fontWeight,
      fontStyle = 'normal',
      color,
      component,
      className,
      children,
      content,
      style,
      ...props
    },
    ref
  ) => {
    // Determine the HTML element to render
    const Element = component || VARIANT_ELEMENT_MAP[variant] || 'p'

    // Determine if we should apply default spacing (only for explicit paragraph components)
    const shouldApplySpacing = component === 'p' || (!component && Element === 'p')

    // Handle color - if it's a predefined color use Tailwind class, otherwise use inline style
    const isPreDefinedColor = color && COLOR_MAP[color]
    const inlineStyle = color && !isPreDefinedColor ? { ...style, color } : style

    // Build className string
    const classes = classNames(
      // Typography variant class (from Tailwind config)
      variant,
      // Font weight class if specified
      fontWeight && FONT_WEIGHT_MAP[fontWeight],
      // Font style class
      FONT_STYLE_MAP[fontStyle],
      // Color class if specified and predefined
      isPreDefinedColor && COLOR_MAP[color],
      // Variant-specific spacing (only for block elements and if not overridden by custom className)
      shouldApplySpacing &&
        !className?.includes('m-') &&
        !className?.includes('mb-') &&
        VARIANT_SPACING_MAP[variant],
      // Custom className
      className
    )

    // Content can be passed as either 'content' prop or 'children'
    const textContent = content || children

    return (
      <Element ref={ref} className={classes} style={inlineStyle} {...props}>
        {textContent}
      </Element>
    )
  }
)

Typography.displayName = 'Typography'

export { Typography }
